<template>
  <!-- ปุ่ม/ลิงก์ปกติ (ไม่ใช่ดรอปดาวน์) -->
  <component
    v-if="!dropdown"
    :is="tag"
    :href="isLink ? href : undefined"
    :target="isLink ? target : undefined"
    :rel="isLink ? relComputed : undefined"
    :class="['btn_shadow', bClass, { disabled }]"
    :role="!isLink ? 'button' : undefined"
    :aria-disabled="disabled ? 'true' : undefined"
    :disabled="!isLink && disabled"
    @click="onClick"
  >
    <component
      v-if="icon"
      :is="icon"
      :class="[iClass ? iClass : 'w-[24px] h-[24px]']"
    />
    <!-- <span v-if="label" :class="['PC_Body2-R truncate', lClass, { disabled }]">
      {{ label }}
    </span> -->
    <span
      v-if="label"
      :class="[
        'PC_Body2-R',
        'whitespace-pre-line', // แปลง \n เป็นขึ้นบรรทัด
        'break-normal', // ตัดเฉพาะตอนมีช่องว่าง/ขีด
        'leading-snug',
        'block', // ให้ขึ้นบรรทัดได้ชัดเจน
        lClass,
        { disabled },
      ]"
    >
      {{ label }}
    </span>
  </component>

  <!-- ปุ่มที่เป็นดรอปดาวน์ -->
  <div v-else ref="root" class="w-full sm:w-fit relative inline-block">
    <!-- trigger -->
    <component
      :is="tag"
      :href="isLink ? href : undefined"
      :target="isLink && target ? target : undefined"
      :rel="isLink ? relComputed : undefined"
      :class="['btn_shadow', bClass, { disabled }]"
      :role="!isLink ? 'button' : undefined"
      :aria-disabled="disabled ? 'true' : undefined"
      :disabled="!isLink && disabled"
      :aria-haspopup="'menu'"
      :aria-expanded="String(isOpen)"
      @click="onClick"
      @keydown="onTriggerKeydown"
    >
      <component
        v-if="icon"
        :is="icon"
        :class="[iClass ? iClass : 'w-[24px] h-[24px]']"
      />
      <span v-if="label" :class="['PC_Body2-R truncate', lClass, { disabled }]">
        {{ label }}
      </span>
    </component>

    <!-- เมนูดรอปดาวน์ -->
    <div
      v-if="isOpen"
      class="absolute z-50 mt-[8px] w-full min-w-[210px] rounded-[6px] border-[1px] border-solid border-[#B7B2B5] bg-[#fff] overflow-hidden"
      :class="dropdownClass ? dropdownClass : 'left-[0px]'"
      role="menu"
      @keydown.stop="onMenuKeydown"
    >
      <div
        v-for="(item, index) in optionsList"
        :key="item.key ?? item.value ?? item.label ?? index"
        class="group flex items-start gap-3 py-[10px] px-[8px] text-[#1F1D1F] hover:bg-[#FBEFF8] cursor-pointer"
        :class="{ 'is-disabled': item.disabled }"
        role="menuitem"
        :aria-disabled="item.disabled ? 'true' : 'false'"
        :tabindex="item.disabled ? -1 : 0"
        @click.stop="() => !item.disabled && onSelect(item, index)"
      >
        <span
          class="PC_Body1-R"
          :class="[
            { 'group-hover:text-[#9B237B]': !item.disabled },
            dropdownListLClass,
          ]"
        >
          {{ item.label ?? String(item) }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount, nextTick } from "vue";

const props = defineProps({
  icon: { type: [String, Object], default: undefined },
  label: { type: String, default: "" },
  lClass: { type: String, default: "" },
  bClass: { type: [String, Object], default: "" },
  iClass: { type: String, default: "" },
  disabled: { type: Boolean, default: false },

  href: { type: String, default: "" },
  extraHref: { type: String, default: "" },
  target: { type: String },
  rel: { type: String, default: "" },

  // dropdown
  dropdown: { type: Boolean, default: false },
  dropdownClass: { type: String, default: "" },
  dropdownList: { type: Array, default: () => [] },
  dropdownListLClass: { type: String, default: "" },

  // ควบคุมเปิด/ปิดจากภายนอก (v-model:open)
  open: { type: Boolean, default: undefined },
  closeOnSelect: { type: Boolean, default: true },
});

const emit = defineEmits(["clicked", "select", "update:open"]);

const isLink = computed(() => !!props.href);
const tag = computed(() => (isLink.value ? "a" : "button"));
const relComputed = computed(
  () =>
    props.rel || (props.target === "_blank" ? "noopener noreferrer" : undefined)
);

const root = ref(null);

// controlled/uncontrolled isOpen
const internalOpen = ref(false);
const isControlled = computed(() => props.open !== undefined);
const isOpen = computed({
  get: () => (isControlled.value ? props.open : internalOpen.value),
  set: (v) => {
    if (isControlled.value) emit("update:open", v);
    else internalOpen.value = v;
  },
});

// ทำ optionsList ที่คงค่า disabled
const optionsList = computed(() => {
  const arr = Array.isArray(props.dropdownList) ? props.dropdownList : [];
  return arr.map((it) =>
    typeof it === "string"
      ? { label: it, value: it, disabled: false }
      : { disabled: false, ...it }
  );
});

function focusFirstEnabledItem() {
  nextTick(() => {
    const items = root.value?.querySelectorAll('[role="menuitem"]');
    if (!items || !items.length) return;
    const firstEnabled = [...items].find(
      (el) => el.getAttribute("aria-disabled") !== "true"
    );
    firstEnabled?.focus?.();
  });
}

function toggleOpen(next) {
  if (!props.dropdown || props.disabled) return;
  const v = typeof next === "boolean" ? next : !isOpen.value;
  isOpen.value = v;
  if (v) focusFirstEnabledItem();
}

function onClick(e) {
  if (props.disabled) {
    e.preventDefault();
    e.stopPropagation();
    return;
  }

  if (props.dropdown) {
    e.preventDefault();
    toggleOpen();
  }

  if (props.extraHref) {
    window.open(props.extraHref, "_blank", "noopener,noreferrer");
  }

  emit("clicked");
}

function onSelect(item, index) {
  emit("select", { item, index });
  if (props.closeOnSelect) toggleOpen(false);
}

function handleOutside(e) {
  if (!root.value) return;
  if (!root.value.contains(e.target)) toggleOpen(false);
}

function onGlobalKeydown(e) {
  if (e.key === "Escape") toggleOpen(false);
}

function onTriggerKeydown(e) {
  if (props.disabled) return;
  if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    toggleOpen(true);
  } else if (e.key === "Escape") {
    toggleOpen(false);
  }
}

function onMenuKeydown(e) {
  const all = [...(root.value?.querySelectorAll('[role="menuitem"]') || [])];
  if (!all.length) return;
  const enabled = all.filter(
    (el) => el.getAttribute("aria-disabled") !== "true"
  );
  if (!enabled.length) return;

  const currentIdx = enabled.indexOf(document.activeElement);

  if (e.key === "ArrowDown") {
    e.preventDefault();
    const next = enabled[(currentIdx + 1) % enabled.length] || enabled[0];
    next?.focus?.();
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    const prev =
      enabled[(currentIdx - 1 + enabled.length) % enabled.length] ||
      enabled.at(-1);
    prev?.focus?.();
  } else if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    const idxInAll = all.indexOf(document.activeElement);
    const item = optionsList.value[idxInAll];
    if (item && !item.disabled) onSelect(item, idxInAll);
  } else if (e.key === "Escape") {
    toggleOpen(false);
  }
}

onMounted(() => {
  document.addEventListener("click", handleOutside, true);
  document.addEventListener("keydown", onGlobalKeydown, true);
});
onBeforeUnmount(() => {
  document.removeEventListener("click", handleOutside, true);
  document.removeEventListener("keydown", onGlobalKeydown, true);
});
</script>

<style lang="scss" scoped>
/* รายการที่ถูกปิดใช้งานในเมนู */
.is-disabled {
  pointer-events: none;
  cursor: not-allowed;
  color: #dfdcdd;
  &:hover {
    background: #fff !important;
  }
}

/* ปุ่มเดิมของคุณ */
.btn_shadow {
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  text-decoration: none;
  border-radius: 6px;
  border: 1px solid;

  &--1 {
    padding: 12px 14px;
    border-color: #9b237b;
    background: #9b237b;
    color: #fff;
  }
  &--12 {
    padding: 10px 20px;
    border-color: #9b237b;
    background: #9b237b;
    color: #fff;
  }
  &--12s {
    width: 100%;
    height: 42px;
    padding: 20px 0px;
    border-color: #9b237b;
    background: #9b237b;
    color: #fff;
    span {
      color: #fff;
      text-align: center;
      font-family: Kanit;
      font-size: 13px;
      font-weight: 400;
      line-height: 20px;
      letter-spacing: 0.1px;
    }
  }
  &--2,
  &--244 {
    width: 36px;
    height: 36px;
    border-color: #b7b2b5;
    background: #fff;
    color: #9b237b !important;
    // svg {
    //   width: 20px !important;
    //   height: 20px !important;
    // }
    &.disabled {
      pointer-events: none;
      user-select: none;
      border-color: #dfdcdd;
      background: #fff !important;
      color: #dfdcdd !important;
      span {
        color: #b7b2b5;
      }
    }
  }
  &--2 {
    svg {
      width: 20px !important;
      height: 20px !important;
    }
  }
  &--244 {
    svg {
      width: 24px !important;
      height: 24px !important;
    }
  }
  &--preAndNext {
    width: 100%;
    max-width: 81px;
    height: 42px;
    padding: 20px 14px;
    border-color: #b7b2b5;
    background: #fff;
    color: #9b237b;
    span {
      color: #1f1d1f;
      text-align: center;
      /* Button/s */
      font-family: Kanit;
      font-size: 13px;
      font-style: normal;
      font-weight: 400;
      line-height: 20px; /* 153.846% */
      letter-spacing: 0.1px;
    }
    &.disabled {
      border: 1px solid #dfdcdd !important;
      background: #fff !important;
      span {
        color: #dfdcdd !important;
      }
    }
  }
  &--3 {
    width: 100%;
    // width: calc(48% - 0rem);
    padding: 20px 14px;
    max-height: 66px;
    border-color: #b7b2b5;
    background: #fff;
    color: #9b237b;
    span {
      color: #1f1d1f;
      text-align: center;
      font-family: Kanit;
      font-size: 14px;
      font-weight: 400;
      line-height: 24px;
      letter-spacing: 0.1px;
    }
    // @media (max-width: 1200px) {
    //   width: 100%;
    // }
    &.disabled {
      border: 1px solid #dfdcdd !important;
      background: #fff !important;
      svg {
        color: #dfdcdd !important;
      }
      span {
        color: #dfdcdd !important;
      }
    }
  }
  &--31 {
    width: 100%;
    // width: calc(48% - 0rem);
    padding: 20px 14px;
    background: #9b237b;
    color: #fff;
    span {
      color: #fff;
      text-align: center;
      font-family: Kanit;
      font-size: 14px;
      font-weight: 400;
      line-height: 24px;
      letter-spacing: 0.1px;
    }
    // @media (max-width: 1200px) {
    //   width: 100%;
    // }
    &.disabled {
      border: 1px solid #dfdcdd !important;
      background: #fff !important;
      svg {
        color: #dfdcdd !important;
      }
      span {
        color: #dfdcdd !important;
      }
    }
  }
  &--32 {
    width: 100%;
    height: 42px;
    padding: 20px 0px;
    border-color: #b7b2b5;
    background: #fff;
    span {
      color: #1f1d1f;
      text-align: center;
      font-family: Kanit;
      font-size: 13px;
      font-weight: 400;
      line-height: 20px;
      letter-spacing: 0.1px;
    }
    @media (max-width: 1200px) {
      width: 100%;
    }
    svg {
      width: 16px;
      height: 16px;
      min-width: 16px;
      min-height: 16px;
      color: #9b237b;
    }
    &.disabled {
      border-color: #dfdcdd !important;
      background: #fff !important;
      span {
        color: #dfdcdd !important;
      }
    }
  }
  &--33 {
    width: fit-content;
    height: 50px;
    padding: 20px 14px;
    border-color: #b7b2b5;
    background: #fff;
    color: #9b237b;
    span {
      color: #1f1d1f;
      text-align: center;
      font-family: Kanit;
      font-size: 14px;
      font-weight: 400;
      line-height: 24px;
      letter-spacing: 0.1px;
    }
    @media (max-width: 1200px) {
      width: 100%;
    }
    &.disabled {
      border: 1px solid #dfdcdd !important;
      background: #fff !important;
      svg {
        color: #dfdcdd !important;
      }
      span {
        color: #dfdcdd !important;
      }
    }
  }
  &--remove {
    padding: 12px 14px;
    border-color: #b7b2b5;
    background: #fff;
    color: #9b237b;
    span {
      color: #1f1d1f !important;
    }
  }
  &--cancel,
  &--onlyCancel {
    width: 100%;
    max-width: 150px;
    border-color: #b7b2b5;
    background: #fff;
    color: #1f1d1f;
  }
  &--cancel {
    @media screen and (max-width: 40rem) {
      max-width: unset;
    }
  }
  &--save,
  &--onlySave {
    width: 100%;
    max-width: 150px;
    border-color: #9b237b;
    background: #9b237b;
    color: #fff;
  }
  &--save {
    @media screen and (max-width: 40rem) {
      max-width: unset;
    }
  }
  &--removeAll {
    height: 42px;
    padding: 12px 20px;
    border-color: #b7b2b5;
    background: #fff;
    span {
      color: #1f1d1f;
      text-align: center;
      /* Button/s */
      font-family: Kanit;
      font-size: 13px;
      font-style: normal;
      font-weight: 400;
      line-height: 20px; /* 153.846% */
      letter-spacing: 0.1px;
    }
    &.disabled {
      background: #fff !important;
      border-color: #dfdcdd !important;
      span {
        color: #dfdcdd !important;
      }
    }
  }
  &--cancel,
  &--onlyCancel,
  &--save,
  &--onlySave {
    height: 50px;
    padding: 12px 20px;
  }
  &--gis {
    width: fit-content;
    // max-width: 146px;
    padding: 11px 16px;
    border-color: #b7b2b5;
    background: #fff;
    color: #9b237b;
    span {
      color: #1f1d1f;
      text-align: center;
      text-overflow: unset !important;

      font-family: Kanit;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 21px; /* 150% */
      letter-spacing: 0.1px;
      @media screen and (max-width: 640px) {
        font-size: 13px !important;
        font-weight: 400 !important;
      }
    }
  }
  &.disabled {
    pointer-events: none;
    user-select: none;
    border-color: #dfdcdd;
    background: #dfdcdd;
    color: #b7b2b5;
    span {
      color: #b7b2b5;
    }
  }
}

.textButtonS {
  text-align: center !important;
  /* Button/s */
  font-family: Kanit !important;
  font-size: 13px !important;
  font-style: normal !important;
  font-weight: 400 !important;
  line-height: 20px !important; /* 153.846% */
  letter-spacing: 0.1px !important;
  &--white {
    span {
      color: #fff !important;
      &:disabled {
        color: #b7b2b5 !important;
      }
    }
  }
}

.textButtonM {
  text-align: center !important;
  /* Button/M */
  font-family: Kanit !important;
  font-size: 14px !important;
  font-style: normal !important;
  font-weight: 400 !important;
  line-height: 21px !important; /* 150% */
  letter-spacing: 0.1px !important;
  &--dark {
    color: #1f1d1f !important;
  }
}

.textButtonL {
  text-align: center !important;

  /* Button/L */
  font-family: Kanit !important;
  font-size: 16px !important;
  font-style: normal !important;
  font-weight: 400 !important;
  line-height: 24px !important; /* 150% */
  letter-spacing: 0.1px !important;
  &--dark {
    color: #1f1d1f;
  }
}

.shadow_B10-O8 {
  /* shadow_B10-O8 */
  box-shadow: 0 4px 10px 0 rgba(0, 0, 0, 0.08);
}

.uploadButtonDropdown1 {
  left: 0px;
  right: unset;
  @media screen and (min-width: 451px) {
    left: unset;
    right: 0px;
  }
}
.uploadButtonDropdown2 {
  left: 0px;
  right: unset;
  @media screen and (min-width: 451px) {
    left: unset;
    right: 0px;
  }
  @media screen and (min-width: 640px) {
    left: 0px;
    right: unset;
  }
  @media screen and (min-width: 1277px) {
    left: unset;
    right: 0px;
  }
}
</style>
